import { t } from "@lingui/macro";
import { useAtomValue } from "jotai";
import { Suspense, useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  RelayEnvironmentProvider,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { Loader } from "@whoosh/design-system/src/components/display";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";

import { isVerifyingEmailOrPhoneAtom } from "./atoms/stores";
import {
  ManageNotifications,
  ManageNotificationsQueryString,
} from "./components/ManageNotifications";
import { ManageNotificationsHeader } from "./components/ManageNotificationsHeader";
import { VerifyEmailOrPhoneNotifications } from "./components/VerifyEmailOrPhoneNotifications";
import { ManageNotificationsQuery } from "./components/__generated__/ManageNotificationsQuery.graphql";

type ManageNotificationsModalProps = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  onVerificationComplete?: () => void;
};

export const ManageNotificationsModal = ({
  isOpen,
  setIsOpen,
  onVerificationComplete,
}: ManageNotificationsModalProps) => {
  const [queryRef, loadQuery] = useQueryLoader<ManageNotificationsQuery>(
    ManageNotificationsQueryString
  );
  const environment = useRelayEnvironment();

  const reloadQuery = useCallback(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  useEffect(() => {
    reloadQuery();
  }, [reloadQuery]);

  return (
    <WithRealSafeAreaInsets>
      <StandardModal
        isOpen={isOpen}
        content={
          <RelayEnvironmentProvider environment={environment}>
            <Suspense fallback={<Loader />}>
              {queryRef ? (
                <ModalWrapper
                  queryRef={queryRef}
                  setIsOpen={setIsOpen}
                  reloadQuery={reloadQuery}
                  onVerificationComplete={onVerificationComplete}
                />
              ) : null}
            </Suspense>
          </RelayEnvironmentProvider>
        }
      />
    </WithRealSafeAreaInsets>
  );
};

type ModalWrapperProps = {
  queryRef: PreloadedQuery<ManageNotificationsQuery>;
  setIsOpen: (val: boolean) => void;
  reloadQuery: () => void;
  onVerificationComplete?: () => void;
};

const ModalWrapper = ({
  queryRef,
  setIsOpen,
  reloadQuery,
  onVerificationComplete,
}: ModalWrapperProps) => {
  const data = usePreloadedQuery(ManageNotificationsQueryString, queryRef);

  const isVerifyingEmailOrPhone = useAtomValue(isVerifyingEmailOrPhoneAtom);
  return (
    <Box flex={1} pt={8} px={6} pb={6}>
      <Suspense>
        {queryRef ? (
          <ManageNotificationsHeader
            sessionRef={data.session ?? null}
            setIsOpen={setIsOpen}
          />
        ) : null}
      </Suspense>
      {isVerifyingEmailOrPhone ? (
        <Suspense>
          {data.session?.user ? (
            <VerifyEmailOrPhoneNotifications
              fragRef={data.session}
              reloadQuery={reloadQuery}
              onVerificationComplete={onVerificationComplete}
            />
          ) : null}
        </Suspense>
      ) : (
        <Suspense fallback={<Loader />}>
          <Text variant={"mainTitle2"} pt={6}>
            {t`Notifications`}
          </Text>
          {queryRef ? (
            <ManageNotifications queryRef={queryRef} setIsOpen={setIsOpen} />
          ) : null}
        </Suspense>
      )}
    </Box>
  );
};
